// Native push registration flow (Capacitor + APNs on iOS, FCM on Android).
// Web browsers keep using VAPID web-push via /lib/push.js. Registers
// listeners BEFORE calling register() so the token event can't be missed.
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";

export function isCapacitorIOS() {
  try { return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios"; }
  catch { return false; }
}

export function isCapacitorAndroid() {
  try { return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android"; }
  catch { return false; }
}

export function isCapacitorPushNative() {
  return isCapacitorIOS() || isCapacitorAndroid();
}

// Module-level once-guard for LISTENER registration only. `register()`
// itself is safe to call multiple times per app launch and is what the
// user's "enable" tap should re-trigger if Apple never delivered a
// token the first time (common on first install, network hiccup, or a
// stale provisioning profile). Splitting these two responsibilities was
// the previous bug: a background call from auth.jsx set `started=true`
// and every subsequent user tap silently no-op'd.
let listenersRegistered = false;
let listeners = [];
export const APNS_ERROR_KEY = "calofay:apns_last_error";

async function _ensureListeners(PushNotifications) {
  if (listenersRegistered) return;
  listenersRegistered = true;

  listeners.push(await PushNotifications.addListener("registration", async (token) => {
    try {
      if (isCapacitorAndroid()) {
        // FCM token — one endpoint, no sandbox/production split.
        await api.post("/push/fcm/devices", { token: token.value, tz_offset_min: -new Date().getTimezoneOffset() });
        console.log("[fcm] registered", token.value.slice(0, 12));
      } else {
        // TestFlight + Xcode debug builds hit APNs *sandbox*; only App
        // Store distribution builds hit production. Since our web bundle
        // is built with NODE_ENV=production for both, we can't rely on
        // env vars — so we register the same token under BOTH envs.
        await api.post("/push/apns/devices", { token: token.value, environment: "sandbox", tz_offset_min: -new Date().getTimezoneOffset() });
        await api.post("/push/apns/devices", { token: token.value, environment: "production", tz_offset_min: -new Date().getTimezoneOffset() });
        console.log("[apns] registered", token.value.slice(0, 12));
      }
      try { localStorage.removeItem(APNS_ERROR_KEY); } catch {}
    } catch (e) {
      const msg = e?.response?.status
        ? `apns register ${e.response.status}: ${e.response.data?.detail || "server error"}`
        : `apns register failed: ${e?.message || "unknown"}`;
      console.error("[apns] register failed", msg, e);
      try { localStorage.setItem(APNS_ERROR_KEY, msg); } catch {}
      // No toast — the error is surfaced in the Push Settings section
      // via getLastApnsError() so we don't spam the user on every
      // launch if the server hiccups mid-boot (e.g., token expired).
    }
  }));

  listeners.push(await PushNotifications.addListener("registrationError", async (e) => {
    // Apple refused to hand out a token. Common causes: Push
    // Notifications capability missing from the provisioning
    // profile, no APNs cert/key uploaded to Apple Developer, or
    // the app was installed in Airplane mode. Surface this loudly.
    const msg = `apns Apple refused: ${e?.error || JSON.stringify(e)}`;
    console.error("[apns] registrationError", msg);
    try { localStorage.setItem(APNS_ERROR_KEY, msg); } catch {}
    // Silent — surfaced inside the Push Settings section via
    // getLastApnsError() rather than as a launch-time toast.
  }));

  listeners.push(await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    const url = notification?.data?.url;
    if (typeof url === "string") window.location.assign(url);
  }));
}

export async function initIOSPush() {
  if (!isCapacitorPushNative()) return { ok: false, reason: "not-native" };

  const { PushNotifications } = await import("@capacitor/push-notifications");
  await _ensureListeners(PushNotifications);

  let status = await PushNotifications.checkPermissions();
  if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
    status = await PushNotifications.requestPermissions();
  }
  if (status.receive !== "granted") return { ok: false, reason: "denied" };

  // Always call register() — even if it was called before this app
  // launch — so user taps on "enable" can retry after any transient
  // failure. Apple/Google will re-deliver the same token via the
  // `registration` listener (or a fresh one if it rotated).
  await PushNotifications.register();
  return { ok: true };
}

// Alias so callers can rename over time without breaking imports.
export const initNativePush = initIOSPush;

export function getLastApnsError() {
  try { return localStorage.getItem(APNS_ERROR_KEY) || ""; } catch { return ""; }
}

export function teardownIOSPush() {
  listeners.forEach((l) => l.remove?.());
  listeners = [];
  listenersRegistered = false;
}
